<template>
  <div v-if="ready" ref="strip" class="s-fleet-leasing">
    <div class="page-container">
      <div class="s-fleet-leasing__header mb-30">
        <div class="h2">{{ title }}</div>

        <div v-if="showClearFilters" class="d-none d-lg-flex">
          <button
            type="button"
            class="btn btn-clear btn-icon-start"
            @click="onClickClearFilters">
            <i class="icon-bin"></i>
            {{ dictionary.buttonClearFiltersText }}
          </button>
        </div>
      </div>

      <div class="b-accordion" :class="{ 'is-active': showAccordionBody }">
        <div class="b-accordion__header" @click="onClickAccordionHeader">
          <span>{{ dictionary.accordionFiltersTitle }}</span>
          <i class="icon-arrow-dropdown-down"></i>
        </div>
        <div v-show="showAccordionBody" class="b-accordion__body">
          <div class="row">
            <div class="col-lg mb-30">
              <FilterBrand
                v-if="records"
                ref="filterBrand"
                :records="records"
                @change-selected="onChangeBrand" />
            </div>
            <div class="col-lg mb-30">
              <FilterModel
                v-if="records"
                ref="filterModel"
                :records="records"
                @change-selected="onChangeModel" />
            </div>
            <div class="col-lg mb-30">
              <FilterFuel
                v-if="records"
                ref="filterFuel"
                :records="records"
                @change-selected="onChangeFuel" />
            </div>
            <div class="col-lg mb-30">
              <FilterTransmission
                v-if="records"
                ref="filterTransmission"
                :records="records"
                @change-selected="onChangeTransmission" />
            </div>
            <div class="col-lg mb-30">
              <FilterPrice
                v-if="records"
                ref="filterPrice"
                :records="records"
                :min-value="priceMinValue"
                :max-value="priceMaxValue"
                @change-selected="onChangePrice" />
            </div>
          </div>

          <div>
            <div v-if="showClearFilters" class="d-flex d-lg-none mb-20">
              <button
                type="button"
                class="btn btn-clear btn-icon-start"
                @click="onClickClearFilters">
                <i class="icon-bin"></i>
                {{ dictionary.buttonClearFiltersText }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="s-fleet-leasing__separator mb-30"></div>

      <div class="row s-fleet-leasing__tools">
        <div class="col-md-6 col-xl-8 mb-30 d-flex align-items-center">
          <strong>
            {{ recordsCountFiltered }} {{ dictionary.resultsFoundSuffix }}
          </strong>
        </div>
        <div class="col-md-6 col-xl-4">
          <div class="row">
            <div class="col-8 col-lg-9 mb-20" style="flex: 1">
              <select
                v-model="sort"
                class="form-select form-select-short"
                @change="onChangeSort">
                <option
                  v-for="item in sortOptions"
                  :key="item.value"
                  :value="item.value">
                  {{ item.text }}
                </option>
              </select>
            </div>
            <div class="col-4 col-lg-3 mb-20" style="flex-basis: fit-content">
              <select
                v-model="itemsPerPage"
                class="form-select form-select-short"
                @change="onChangeItemsPerPage">
                <option
                  v-for="item in itemsPerPageOptions"
                  :key="item.value"
                  :value="item.value">
                  {{ item.text }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="recordsCountFiltered"
        ref="grid"
        v-auto-animate
        class="s-fleet-leasing__grid mb-60">
        <div
          v-for="item in recordsFilteredPaged"
          :key="item.id"
          class="s-fleet-leasing__item">
          <VehicleLeasing
            v-if="item.documentTypeAlias === defaultOfferType"
            :item="item"
            :is-already-checked="isChecked.indexOf(item.id.toString()) !== -1"
            :show-compare="true" />
          <VehicleSaverLeasing
            v-if="item.documentTypeAlias === saverOfferType"
            :item="item"
            :is-already-checked="isChecked.indexOf(item.id.toString()) !== -1"
            :show-compare="true" />
        </div>
      </div>

      <div v-if="!recordsCountFiltered">
        <NoResults></NoResults>
      </div>

      <div
        v-if="recordsCountFiltered"
        class="d-flex justify-content-center mb-40">
        <Pagination
          :total-records="recordsCountFiltered"
          :per-page="itemsPerPage"
          :current-page="page"
          @pagechanged="pageChanged" />
      </div>

      <section
        v-if="dictionary.disclaimerDescription"
        class="s-contact-note mb-20">
        <div class="s-contact-note__body">
          <div class="s-contact-note__details">
            <div class="s-contact-note__picture">
              <IdeaLottie width="20"></IdeaLottie>
            </div>

            <div>
              <div class="s-contact-note__sub-title">
                {{ dictionary.disclaimerTitle }}
              </div>
              <div class="s-contact-note__description">
                {{ dictionary.disclaimerDescription }}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
  <Transition name="slide-up">
    <VehiclesCompareBar
      v-if="recordsCompare.length"
      class="d-lg-block d-none"
      :records="recordsCompare" />
  </Transition>
</template>

<script>
import jQuery from 'jquery'
import * as lib from '../../lib/lib'
import VehicleLeasing from '../components/VehicleLeasing.vue'
import VehicleSaverLeasing from '../components/VehicleSaverLeasing.vue'
import NoResults from '../components/common/NoResults.vue'
import Pagination from './../components/common/Pagination.vue'
/* eslint-disable no-unused-vars */
import { compact, max, min, sortBy, uniqBy } from 'lodash'
import FilterBrand from './../components/leasingOffers/FilterBrand.vue'
import FilterModel from './../components/leasingOffers/FilterModel.vue'
import FilterTransmission from './../components/leasingOffers/FilterTransmission.vue'
import FilterFuel from './../components/leasingOffers/FilterFuel.vue'
import FilterPrice from './../components/leasingOffers/FilterPrice.vue'
import mixinsLeasingOffers from './../mixins/leasingOffers-mixins'
import constants from './../../lib/constants'
import VehiclesCompareBar from './../components/leasingOffers/VehiclesCompareBar.vue'
import IdeaLottie from './../components/lotties/Idea.vue'

const { filters, offerType } = constants.leasing

export default {
  components: {
    NoResults,
    VehicleLeasing,
    VehicleSaverLeasing,
    Pagination,
    FilterBrand,
    FilterModel,
    FilterTransmission,
    FilterFuel,
    FilterPrice,
    VehiclesCompareBar,
    IdeaLottie,
  },
  mixins: [mixinsLeasingOffers],
  data() {
    return {
      ready: false,
      title: '',
      records: [],
      recordsFiltered: [],
      page: 1,
      itemsPerPage: 0,
      sort: '',
      showAccordionBody: false,
      // data sources
      sortOptions: [],
      itemsPerPageOptions: [],
    }
  },
  computed: {
    isChecked() {
      if (!lib.getUrlParam('vehicles')) {
        return []
      }
      return lib.getUrlParam('vehicles').split(',')
    },

    defaultOfferType() {
      return offerType.default
    },

    saverOfferType() {
      return offerType.saver
    },

    showClearFilters() {
      return (
        this.brands.length ||
        this.models.length ||
        this.fuels.length ||
        this.transmissions.length ||
        (this.prices.length && this.pricesHaveChanged)
      )
    },

    pricesHaveChanged() {
      return (
        this.prices[0] !== this.priceMinValue ||
        this.prices[1] !== this.priceMaxValue
      )
    },

    recordsFilteredPaged() {
      const start = (this.page - 1) * this.itemsPerPage
      const end = this.page * this.itemsPerPage
      return this.recordsFiltered.slice(start, end)
    },

    recordsCountFiltered() {
      return this.recordsFiltered.length
    },

    priceMinValue() {
      const { priceInterval } = this.configuration
      const items = this.records.map((i) => i.pricePerMonthInteger)

      /* eslint-disable no-undef */
      return Math.floor(_.min(items) / priceInterval) * priceInterval
    },

    priceMaxValue() {
      const { priceInterval } = this.configuration
      const items = this.records.map((i) => i.pricePerMonthInteger)

      /* eslint-disable no-undef */
      return Math.ceil(_.max(items) / priceInterval) * priceInterval
    },
  },
  mounted() {
    const { title, records, sortOptions, itemsPerPageOptions } =
      lib.getData('fleetData')
    this.title = title
    this.records = this.recordsFiltered = records
    this.sortOptions = sortOptions
    this.itemsPerPageOptions = itemsPerPageOptions

    this.addOriginalIndex()
    this.setPreselectedOptions()
    this.setPreselectedValues()

    this.ready = true
    this.accordionIsActive = lib.scrDesktops()
  },
  methods: {
    onClickAccordionHeader() {
      this.showAccordionBody = !this.showAccordionBody
    },

    addOriginalIndex() {
      /* eslint-disable no-undef */
      _.forEach(this.records, (x, index) => {
        x.originalIndex = index
      })
    },

    onChangeBrand(value) {
      lib.appendUrlParam(filters.brands, value.join(','))
      this.setField({ field: filters.brands, value })
      if (!value.length) {
        this.$refs.filterModel.resetSelected()
      }

      this.filterRecords()
    },

    onChangeModel(value) {
      lib.appendUrlParam(filters.models, value.join(','))
      this.setField({ field: filters.models, value })
      this.filterRecords()
    },

    onChangeFuel(value) {
      lib.appendUrlParam(filters.fuels, value.join(','))
      this.setField({ field: filters.fuels, value })
      this.filterRecords()
    },

    onChangeTransmission(value) {
      lib.appendUrlParam(filters.transmissions, value.join(','))
      this.setField({ field: filters.transmissions, value })
      this.filterRecords()
    },

    onChangePrice(value) {
      lib.appendUrlParam(filters.prices, value.length ? value.join(',') : '')
      this.setField({ field: filters.prices, value })
      this.filterRecords()
    },

    filterRecords() {
      this.page = 1
      let array = this.records

      if (this.brands.length) {
        array = array.filter((i) => this.brands.includes(i.make))
      }

      if (this.models.length) {
        array = array.filter((i) => this.models.includes(i.model))
      }

      if (this.fuels.length) {
        array = array.filter((i) => this.fuels.includes(i.fuelPreValue))
      }

      if (this.transmissions.length) {
        array = array.filter((i) =>
          this.transmissions.includes(i.transmissionTypePreValue)
        )
      }

      if (this.prices.length) {
        array = array.filter(
          (i) =>
            i.pricePerMonthInteger >= this.prices[0] &&
            i.pricePerMonthInteger <= this.prices[1]
        )
      }

      this.recordsFiltered = array
    },

    setPreselectedOptions() {
      // sort
      const entry = this.sortOptions.find((i) => i.selected)
      if (entry) {
        this.sort = entry.value
        this.onChangeSort()
      }
      // items per page
      const entry2 = this.itemsPerPageOptions.find((i) => i.selected)
      if (entry) {
        this.itemsPerPage = entry2.value
      }

      this.itemsPerPageOptions.push({
        text: this.dictionary.itemsPerPageAllLabel,
        value: this.records.length,
      })
    },

    setPreselectedValues() {
      this.setPreselectedBrands()
      this.setPreselectedModels()
      this.setPreselectedTransmissions()
      this.setPreselectedFuels()
      this.setPreselectedPrices()
      this.filterRecords()
    },

    setPreselectedBrands() {
      const field = filters.brands
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.make === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedModels() {
      const field = filters.models
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.model === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedTransmissions() {
      const field = filters.transmissions
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) =>
              this.records.some((x) => x.transmissionTypePreValue === i)
            )
        : []

      this.setField({ field, value })
    },

    setPreselectedFuels() {
      const field = filters.fuels
      const urlParam = lib.getUrlParam(field)
      const value = urlParam
        ? urlParam
            .split(',')
            .filter((i) => this.records.some((x) => x.fuelPreValue === i))
        : []

      this.setField({ field, value })
    },

    setPreselectedPrices() {
      const field = filters.prices
      const urlParam = lib.getUrlParam(field)
      const value = urlParam ? urlParam.split(',') : []

      this.setField({ field, value })
    },

    pageChanged(value) {
      this.page = value
      lib.scrollToElem(jQuery(this.$refs.grid), 100)
    },

    onChangeSort() {
      this.page = 1
      const { alpha, featured } = constants.sort

      let array = this.recordsFiltered

      switch (this.sort) {
        case alpha:
          array = _.sortBy(array, (x) => `${x.make}${x.model}`)
          break
        case featured:
          array = _.sortBy(array, (x) => x.originalIndex)
          break
        default:
          array = _.sortBy(array, (x) => x.originalIndex)
      }

      this.recordsFiltered = array
    },

    onChangeItemsPerPage() {
      this.page = 1
    },

    onClickClearFilters() {
      this.brands = this.models = this.fuels = this.transmissions = []

      this.$refs.filterBrand.resetSelected()
      this.$refs.filterModel.resetSelected()
      this.$refs.filterFuel.resetSelected()
      this.$refs.filterTransmission.resetSelected()
      this.$refs.filterPrice.resetSelected()
    },
  },
}
</script>
